import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Link, Snackbar, Typography } from "@mui/material";
import {
  RiArrowLeftLine,
  RiCalendarCloseLine,
  RiCheckboxCircleLine,
  RiMenLine,
  RiNotification3Line,
  RiParentLine,
  RiTicket2Line,
  RiVideoLine,
  RiWomenLine,
} from "@remixicon/react";
import { countStudentBookings, getChild, getStudentBookingsPage, getStudentChildrenFull, getUserById } from "../../services/dataService";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions } from "../../config/permissions";
import { ageFrom, ageLabel, bornLabel, childCards, childPosition, countLabel, sortChildren } from "../../utils/studentDetail";
import { dateTimeLines, showingLabel } from "../../utils/kitPresentation";
import { tokens } from "../../utils/figmaTokens";
import { useBreadcrumbName } from "../../components/Layout/BreadcrumbContext";
import { InfoItem, InfoRow, PageHead } from "../../components/kit/PageHead";
import { TableCard } from "../../components/kit/SectionCard";
import Pager from "../../components/kit/Pager";
import StatCard from "../../components/kit/StatCard";
import { OpenTicketDialog } from "../Lessons/LessonActions";
import SendNotificationDialog from "./SendNotificationDialog";
import { PAGE_SIZE } from "./tabs/TabState";
import { usePagedQuery } from "./tabs/usePagedQuery";
import { useUsersByIds } from "./tabs/useUsersByIds";
import SessionsTable from "./tabs/SessionsTable";

// The icon on each of the three tiles (044 D37: no Reported tile).
const CARD_ICONS = { taken: RiVideoLine, finished: RiCheckboxCircleLine, cancelled: RiCalendarCloseLine };

const genderIcon = (gender) => (/^f/i.test(gender || "") ? RiWomenLine : /^m/i.test(gender || "") ? RiMenLine : null);

// The design's child profile less what has no data (teachers' notes, progress per
// subject, attendance — backlog, D30), drawn from Figma (047 D13, D17; 10764:83360): no
// white card; the head, three counts (no Reported, D37), the child's sessions on the
// (studentId, learner.childId, startTimeUTC desc) index (D35). The two buttons act on the
// parent's account: a child has none of their own.
const ChildPage = () => {
  const { id, childId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [parent, setParent] = useState(null);
  const [child, setChild] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [dialog, setDialog] = useState(""); // "" | "notify" | "ticket"
  const [snack, setSnack] = useState(null); // { message, severity }

  // The top bar's "Students / parent / child" (047 D11); a crumb the page could not read says
  // "Not found" once it has decided (D83).
  useBreadcrumbName(id, parent ? parent.name || id : missing ? "Not found" : null);
  useBreadcrumbName(childId, child ? child.name || "Child" : missing ? "Not found" : null);

  // The newest child wins: a slow read for the child just left must not land here.
  const latest = useRef("");
  const load = useCallback(async () => {
    const key = `${id}/${childId}`;
    latest.current = key;
    setLoading(true);
    const [p, c, all] = await Promise.all([
      getUserById(id),
      getChild(id, childId).catch((e) => {
        console.error("Error loading the child:", e);
        return null;
      }),
      getStudentChildrenFull(id).catch((e) => {
        console.error("Error loading the children:", e);
        return [];
      }),
    ]);
    if (latest.current !== key) return;
    setParent(p);
    setChild(c);
    setSiblings(sortChildren(all));
    setMissing(!p || !c);
    setLoading(false);
  }, [id, childId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    setCounts({});
    const one = (key, status) =>
      countStudentBookings(id, { childId, status })
        .then((n) => !cancelled && setCounts((k) => ({ ...k, [key]: n })))
        .catch((e) => {
          console.error(`child count ${key} failed:`, e);
          if (!cancelled) setCounts((k) => ({ ...k, [key]: null }));
        });
    one("taken", null);
    one("finished", "Finished");
    one("cancelled", "Cancelled");
    return () => {
      cancelled = true;
    };
  }, [id, childId]);

  const fetchPage = useCallback((cursor) => getStudentBookingsPage(id, { childId, pageSize: PAGE_SIZE, cursor }), [id, childId]);
  const sessions = usePagedQuery(fetchPage, childId, "Failed to load the sessions.");
  const usersById = useUsersByIds(sessions.rows.map((b) => b.teacherId));

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  }
  if (missing) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>Child not found.</Alert>
        <Button component={RouterLink} to={`/students/${id}/children`} sx={{ mt: 2 }}>Back to the children</Button>
      </Box>
    );
  }

  const can = (p) => hasPermission(user, p);
  const notify = (message, severity = "success") => setSnack({ message, severity });
  const details = [child.gender, ageLabel(ageFrom(child.date_of_birth)), bornLabel(child.date_of_birth)].filter(Boolean).join(" · ");
  const sub = [`Added ${dateTimeLines(child.createdAt)?.date || "-"}`, childPosition(siblings, childId)].filter(Boolean).join(" · ");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Link
        component={RouterLink}
        to={`/students/${id}/children`}
        underline="hover"
        sx={{ display: "inline-flex", alignItems: "center", gap: "6px", alignSelf: "flex-start", typography: "bodySmallStrong", color: tokens.text.link }}
      >
        <RiArrowLeftLine size={16} />
        Back to {parent.name || "the parent"} · Children
      </Link>

      <PageHead
        align="center"
        actionsGap="10px"
        name={child.name || "Child"}
        colorKey={childId}
        chips={[{ label: "Child", color: "default" }]}
        lines={
          <InfoRow rowGap="4px">
            {details && <InfoItem Icon={genderIcon(child.gender)}>{details}</InfoItem>}
            <InfoItem Icon={RiParentLine} color={tokens.text.link}>
              <Link component={RouterLink} to={`/students/${id}`} underline="hover" color="inherit">
                Parent: {parent.name || id}
              </Link>
            </InfoItem>
          </InfoRow>
        }
        meta={sub}
        actions={
          <>
            {can(permissions.EDIT_LESSONS) && (
              <Button size="large" variant="outlined" startIcon={<RiTicket2Line size={20} />} onClick={() => setDialog("ticket")}>
                Open ticket
              </Button>
            )}
            {can(permissions.SEND_NOTIFICATIONS) && (
              <Button size="large" variant="contained" startIcon={<RiNotification3Line size={20} />} onClick={() => setDialog("notify")}>
                Send notification
              </Button>
            )}
          </>
        }
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="titleCard" component="h2" sx={{ color: tokens.text.primary }}>
          Sessions
        </Typography>
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(3, minmax(0, 1fr))" } }}>
          {childCards(counts).map((c) => (
            <StatCard key={c.key} Icon={CARD_ICONS[c.key]} label={c.label} value={countLabel(c.value)} />
          ))}
        </Box>
      </Box>

      <TableCard
        title="All sessions"
        subtitle="Newest first"
        footer={
          <Pager
            label={sessions.loading || sessions.error ? "" : showingLabel(sessions.page, sessions.rows.length, PAGE_SIZE)}
            page={sessions.page}
            hasMore={sessions.hasMore}
            onPage={sessions.load}
          />
        }
      >
        <SessionsTable
          rows={sessions.rows}
          childrenById={{ [childId]: child }}
          usersById={usersById}
          holder={parent}
          timeZone={parent.timeZone}
          loading={sessions.loading}
          error={sessions.error}
          onRetry={() => sessions.load(sessions.page)}
          emptyText="No sessions for this child yet."
          forChild
        />
      </TableCard>

      <SendNotificationDialog open={dialog === "notify"} onClose={() => setDialog("")} person={parent} actingUser={user} onSent={(message) => notify(message)} />
      <OpenTicketDialog
        open={dialog === "ticket"}
        onClose={() => setDialog("")}
        booking={null}
        userId={parent.id}
        label=""
        personName={parent.name || "this parent"}
        actingUser={user}
        onDone={(message, ticketId) => {
          notify(message);
          if (ticketId) navigate(`/inbox/${ticketId}`);
        }}
      />
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)}>
        <Alert severity={snack?.severity || "success"} onClose={() => setSnack(null)}>{snack?.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ChildPage;
